import {Chess} from 'chess.js';

/**
 * A line to analyse along: the puzzle's moves as played, then whatever was
 * tried from any point in them. Clicking a move in the notation goes back to
 * that position; a move made there replaces the rest of the line, the way a
 * plain analysis board does — there is no tree to get lost in.
 *
 * Shared by the editor's engine mode and the student's Analyze, so both
 * notations behave the same.
 */
export type AnalysisLine = {
  /** Every position on the line, from the puzzle's start. */
  fens: string[];
  /** The move into each position; null for the start. */
  sans: (string | null)[];
  /** The position on the board. */
  index: number;
  /** How many plies are the puzzle's own; the rest were explored. */
  fromPuzzle: number;
};

export type AnalysisCell = {
  index: number;
  san: string;
  current: boolean;
  explored: boolean;
};

export type AnalysisRow = {
  number: number;
  white: AnalysisCell | null;
  black: AnalysisCell | null;
};

const clamp = (line: AnalysisLine, index: number) =>
  Math.max(0, Math.min(line.fens.length - 1, index));

/**
 * The puzzle's moves, replayed from its start, standing on `index` (the last
 * by default). A move that does not replay ends the line there: the board is
 * never shown a position nobody played.
 */
export function startAnalysis(
  start: string,
  sans: string[],
  index?: number,
): AnalysisLine {
  const fens = [start];
  const played: (string | null)[] = [null];
  try {
    const game = new Chess(start);
    for (const san of sans ?? []) {
      try {
        const move = game.move(san);
        if (!move) break;
        fens.push(game.fen());
        played.push(move.san);
      } catch {
        break;
      }
    }
  } catch {
    /* an unreadable start: the line is just that position */
  }
  const line = {
    fens,
    sans: played,
    index: fens.length - 1,
    fromPuzzle: fens.length - 1,
  };
  return index == null ? line : {...line, index: clamp(line, index)};
}

export function goTo(line: AnalysisLine, index: number): AnalysisLine {
  return {...line, index: clamp(line, index)};
}

export function step(line: AnalysisLine, by: number): AnalysisLine {
  return goTo(line, line.index + by);
}

/** The board: where the line stands, and the position before it. */
export function analysisView(line: AnalysisLine): {
  fen: string;
  lastMoveFen: string;
} {
  return {
    fen: line.fens[line.index],
    lastMoveFen: line.fens[Math.max(0, line.index - 1)],
  };
}

/**
 * A move from where the line stands, for whichever side is to move. The move
 * already next on the line just steps onto it; any other cuts the line there
 * and carries on from the new move. null when it is not legal.
 */
export function playOn(
  line: AnalysisLine,
  from: string,
  to: string,
  promotion = 'q',
): AnalysisLine | null {
  if (!from || !to || from === to) return null;
  let san: string;
  let fen: string;
  try {
    const game = new Chess(line.fens[line.index]);
    const move = game.move({from, to, promotion});
    if (!move) return null;
    san = move.san;
    fen = game.fen();
  } catch {
    return null;
  }
  const next = line.index + 1;
  if (line.sans[next] === san) return {...line, index: next};
  return {
    fens: [...line.fens.slice(0, next), fen],
    sans: [...line.sans.slice(0, next), san],
    index: next,
    fromPuzzle: Math.min(line.fromPuzzle, line.index),
  };
}

/**
 * The line as a scoresheet, numbered from the puzzle's start as the puzzle's
 * own scoresheet is ("1." reads better than the game's "10."), "1…" first
 * when Black moves first.
 */
export function analysisRows(line: AnalysisLine): AnalysisRow[] {
  const blackFirst = (line.fens[0].split(' ')[1] ?? 'w') === 'b';
  const rows: AnalysisRow[] = [];
  for (let i = 1; i < line.sans.length; i++) {
    const at = i - 1 + (blackFirst ? 1 : 0);
    const number = Math.floor(at / 2) + 1;
    let row = rows.find(r => r.number === number);
    if (!row) {
      row = {number, white: null, black: null};
      rows.push(row);
    }
    const cell = {
      index: i,
      san: line.sans[i] as string,
      current: i === line.index,
      explored: i > line.fromPuzzle,
    };
    if (at % 2 === 0) row.white = cell;
    else row.black = cell;
  }
  return rows;
}
